'use client';

import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  FieldValues,
  SubmitHandler,
  useForm
} from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from "react";

import useRentModal from '@/app/hooks/useRentModal';

import Modal from "./Modal";
import Counter from "../inputs/Counter";
import CategoryInput from '../inputs/CategoryInput';
import CountrySelect from "../inputs/CountrySelect";
import { categories } from '../navbar/Categories';
import Input from '../inputs/Input';
import Heading from '../Heading';
import ListingPhotoUpload from '../inputs/editlisting/ListingPhotoUpload';
import { SafeUser, SimplifiedUser } from '@/app/types';
import useCountries from '@/app/hooks/useCountries';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import CurrencySelector, { CurrencyOption } from "./../inputs/CurrencySelector";
import useCurrencies from '@/app/hooks/useCurrencies';
import useCurrencyStore from '@/app/hooks/useCurrencyStore';
import { ExchangeCurrency } from '@prisma/client';
import Textarea from '../inputs/Textarea';

enum STEPS {
  CATEGORY = 0,
  DESCRIPTION = 1,
  IMAGES = 2,
  INFO = 3,
  PRICE = 4,
  INCLUDED = 5,
  NOTINCLUDEDPRICE = 6,
  REQUIREMENTS = 7
}

const options = [
  'Scuba equipment',
  'Dive computer',
  'Weights/Weight Belt/Tanks',
  'Transportation to the dive center',
  'Nitrox',
  '15L tank',
  'Book/e-Learning',
  'Fees',
  'Lunch',
  'Snacks',
  'Water',
  'Coffee/Tea',
];


interface RentModalProps {
  currentUser?: SimplifiedUser | null;
  rates?: ExchangeCurrency | null;
}

const RentModal: React.FC<RentModalProps> = ({ currentUser, rates }) => {
  const router = useRouter();
  const rentModal = useRentModal();
  const [isUploading, setIsUploading] = useState(false);
  const [showExtraFields, setShowExtraFields] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(STEPS.CATEGORY);

  // const { getByName } = useCountries();
  // const defaultLocation = getByName(currentUser?.diveCenter?.country || "");

  const { getByCode } = useCurrencies();
  const defaultCurrency = getByCode(currentUser?.currency || "");
  const currencyStore = useCurrencyStore();
  const selectedCurrency = currencyStore.selectedCurrency;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: {
      errors,
    },
    reset,
  } = useForm<FieldValues>({
    defaultValues: {
      category: '',
      imageSrc: '',
      title: '',
      description: '',
      currency: defaultCurrency,
      price: 0,
      priceInUSD: 0,
      priceExtraDiver: 0,
      priceExtraDiverUSD: 0,
      priceExtraSnorkeler: 0,
      priceExtraSnorkelerUSD: 0,
      priceNitrox: 0,
      priceNitroxInUSD: 0,
      price15L: 0,
      price15LInUSD: 0,
      priceFullSet: 0,
      priceFullSetInUSD: 0,
      priceComputer: 0,
      priceComputerInUSD: 0,
      priceAdapter: 0,
      priceAdapterInUSD: 0,
      priceBCD: 0,
      priceBCDInUSD: 0,
      priceReg: 0,
      priceRegInUSD: 0,
      priceWetsuit: 0,
      priceWetsuitInUSD: 0,
      priceMask: 0,
      priceMaskInUSD: 0,
      priceFins: 0,
      priceFinsInUSD: 0,
      priceWeights: 0,
      priceWeightsInUSD: 0,
      pricePrivateGuide: 0,
      pricePrivateGuideInUSD: 0,
      priceFees: 0,
      priceFeesInUSD: 0,
      startTime: '',
      length: 1,
      lengthUnit: '',
      students: 1,
      included: '',
      notIncluded: '',
      minAge: '',
      minCertification: '',
      minLoggedDives: '',
      city: '',
      country: '',
      numberOfDives: 1,
      boat: '',
    }
  });

  const category = watch('category');
  const length = watch('length');
  const lengthUnit = watch('lengthUnit');
  const students = watch('students');
  const numberOfDives = watch('numberOfDives');
  const boat = watch('boat');
  const currency = watch('currency');
  const startTime = watch('startTime');
  const imageSrc = watch('imageSrc');
  const price = watch('price');
  const priceInUSD = watch('priceInUSD');
  const priceExtraDiver = watch('priceExtraDiver');
  const priceExtraDiverUSD = watch('priceExtraDiverUSD');
  const priceExtraSnorkeler = watch('priceExtraSnorkeler');
  const priceExtraSnorkelerUSD = watch('priceExtraSnorkelerUSD');
  const priceNitrox = watch('priceNitrox');
  const priceNitroxInUSD = watch('priceNitroxInUSD');
  const price15L = watch('price15L');
  const price15LInUSD = watch('price15LInUSD');
  const priceFullSet = watch('priceFullSet');
  const priceFullSetInUSD = watch('priceFullSetInUSD');
  const priceComputer = watch('priceComputer');
  const priceComputerInUSD = watch('priceComputerInUSD');
  const priceAdapter = watch('priceAdapter');
  const priceAdapterInUSD = watch('priceAdapterInUSD');
  const priceBCD = watch('priceBCD');
  const priceBCDInUSD = watch('priceBCDInUSD');
  const priceReg = watch('priceReg');
  const priceRegInUSD = watch('priceRegInUSD');
  const priceWetsuit = watch('priceWetsuit');
  const priceWetsuitInUSD = watch('priceWetsuitInUSD');
  const priceMask = watch('priceMask');
  const priceMaskInUSD = watch('priceMaskInUSD');
  const priceFins = watch('priceFins');
  const priceFinsInUSD = watch('priceFinsInUSD');
  const priceWeights = watch('priceWeights');
  const priceWeightsInUSD = watch('priceWeightsInUSD');
  const pricePrivateGuide = watch('pricePrivateGuide');
  const pricePrivateGuideInUSD = watch('pricePrivateGuideInUSD');
  const priceFees = watch('priceFees');
  const priceFeesInUSD = watch('priceFeesInUSD');
  // const included = watch('included');
  // const notIncluded = watch('notIncluded');
  const minAge = watch('minAge');
  const minCertification = watch('minCertification');
  const minLoggedDives = watch('minLoggedDives');

  const [isTimeDetermined, setIsTimeDetermined] = useState(false);
  // const [length, setLength] = useState(0);
  const [unit, setUnit] = useState('hours');

  const [selectedIncluded, setSelectedIncluded] = useState<string[]>([]);
  const [otherIncluded, setOtherIncluded] = useState<string>('');

  const [selectedNotIncluded, setSelectedNotIncluded] = useState<string[]>([]);
  const [otherNotIncluded, setOtherNotIncluded] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const visibleOptions = showAll ? options : options.slice(0, 3);

  const [noRequirements, setNoRequirements] = useState(true);

  const gearNotIncluded = selectedNotIncluded.includes('Scuba equipment');
  const computerNotIncluded = selectedNotIncluded.includes('Dive computer');
  const nitroxNotIncluded = selectedNotIncluded.includes('Nitrox');
  const l15NotIncluded = selectedNotIncluded.includes('15L tank');
  const weightsNotIncluded = selectedNotIncluded.includes('Weights/Weight Belt/Tanks');
  const feesIncluded = selectedIncluded.includes('Fees');
  const feesNotIncluded = selectedNotIncluded.includes('Fees');

  useEffect(() => {
    setShowExtraFields(category !== 'FUN DIVES' || category !== 'Snorkel');
  }, [category]);

  const onChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomValue('startTime', e.target.value)
  };

  const onToggleTimeDetermined = () => {
    setIsTimeDetermined(prevState => !prevState);
  };

  useEffect(() => {
    if (!isTimeDetermined) {
      setCustomValue('startTime', '');
    }
  }, [isTimeDetermined]);

  useEffect(() => {
    if (unit) {
      setCustomValue('lengthUnit', unit);
    }
  }, [unit]);

  useEffect(() => {
    if (currency?.value && currency?.code !== "USD") {
      if (rates) {
        const exchangeRate = rates[currency?.code as keyof typeof rates];
        if (typeof exchangeRate === 'number') {
          if (price != null) {
            const convertedPrice = (price * exchangeRate).toFixed(2);
            setCustomValue('priceInUSD', convertedPrice)
          }
          if (priceExtraDiver != null) {
            const convertedPriceExtra = (priceExtraDiver * exchangeRate).toFixed(2);
            setCustomValue('priceExtraDiverUSD', convertedPriceExtra)
          }
          if (priceExtraSnorkeler != null) {
            const convertedPriceExtraSnorkeler = (priceExtraSnorkeler * exchangeRate).toFixed(2);
            setCustomValue('priceExtraSnorkelerUSD', convertedPriceExtraSnorkeler)
          }
          if (priceNitrox != null) {
            const convertedPriceNitrox = (priceNitrox * exchangeRate).toFixed(2);
            setCustomValue('priceNitroxInUSD', convertedPriceNitrox)
          }
          if (price15L != null) {
            const convertedPrice15L = (price15L * exchangeRate).toFixed(2);
            setCustomValue('price15LInUSD', convertedPrice15L)
          }
          if (priceFullSet != null) {
            const convertedPriceFullSet = (priceFullSet * exchangeRate).toFixed(2);
            setCustomValue('priceFullSetInUSD', convertedPriceFullSet)
          }
          if (priceComputer != null) {
            const convertedPriceComputer = (priceComputer * exchangeRate).toFixed(2);
            setCustomValue('priceComputerInUSD', convertedPriceComputer)
          }
          if (priceAdapter != null) {
            const convertedPriceAdapter = (priceAdapter * exchangeRate).toFixed(2);
            setCustomValue('priceAdapterInUSD', convertedPriceAdapter)
          }
          if (priceBCD != null) {
            const convertedPriceBCD = (priceBCD * exchangeRate).toFixed(2);
            setCustomValue('priceBCDInUSD', convertedPriceBCD)
          }
          if (priceReg != null) {
            const convertedPriceReg = (priceReg * exchangeRate).toFixed(2);
            setCustomValue('priceRegInUSD', convertedPriceReg)
          }
          if (priceWetsuit != null) {
            const convertedPriceWetsuit = (priceWetsuit * exchangeRate).toFixed(2);
            setCustomValue('priceWetsuitInUSD', convertedPriceWetsuit)
          }
          if (priceMask != null) {
            const convertedPriceMask = (priceMask * exchangeRate).toFixed(2);
            setCustomValue('priceMaskInUSD', convertedPriceMask)
          }
          if (priceFins != null) {
            const convertedPriceFins = (priceFins * exchangeRate).toFixed(2);
            setCustomValue('priceFinsInUSD', convertedPriceFins)
          }
          if (priceWeights != null) {
            const convertedPriceWeights = (priceWeights * exchangeRate).toFixed(2);
            setCustomValue('priceWeightsInUSD', convertedPriceWeights)
          }
          if (pricePrivateGuide != null) {
            const convertedPricePrivateGuide = (pricePrivateGuide * exchangeRate).toFixed(2);
            setCustomValue('pricePrivateGuideInUSD', convertedPricePrivateGuide)
          }
          if (priceFees != null) {
            const convertedPriceFees = (priceFees * exchangeRate).toFixed(2);
            setCustomValue('priceFeesInUSD', convertedPriceFees)
          }
        }
      }

    } else {
      setCustomValue('priceInUSD', price)
      setCustomValue('priceExtraDiverUSD', priceExtraDiver)
      setCustomValue('priceExtraSnorkelerUSD', priceExtraSnorkeler)
      setCustomValue('priceNitroxInUSD', priceNitrox)
      setCustomValue('price15LInUSD', price15L)
      setCustomValue('priceFullSetInUSD', priceFullSet)
      setCustomValue('priceComputerInUSD', priceComputer)
      setCustomValue('priceAdapterInUSD', priceAdapter)
      setCustomValue('priceBCDInUSD', priceBCD)
      setCustomValue('priceRegInUSD', priceReg)
      setCustomValue('priceWetsuitInUSD', priceWetsuit)
      setCustomValue('priceMaskInUSD', priceMask)
      setCustomValue('priceFinsInUSD', priceFins)
      setCustomValue('priceWeightsInUSD', priceWeights)
      setCustomValue('pricePrivateGuideInUSD', pricePrivateGuide)
      setCustomValue('priceFeesInUSD', priceFees)
    }
  }, [currency, price, priceExtraDiver, priceExtraSnorkeler, priceNitrox, price15L, priceFullSet, priceComputer, priceAdapter, priceBCD, priceReg, priceWetsuit, priceMask, priceFins, priceWeights, pricePrivateGuide, priceFees]);

  const setCustomValue = (id: string, value: any) => {
    setValue(id, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    })
  }

  const onBack = () => {
    setStep((value) => {
      const currentStep = value - 1;

      if (currentStep === STEPS.NOTINCLUDEDPRICE) {
        if (!(nitroxNotIncluded || l15NotIncluded || gearNotIncluded || computerNotIncluded || weightsNotIncluded || feesIncluded || feesNotIncluded)) {
          return STEPS.INCLUDED;
        } else {
          return currentStep;
        }
      } else {
        return currentStep;
      }
    });
  }

  const onNext = () => {
    setStep((value) => {
      const currentStep = value + 1;

      if (currentStep === STEPS.NOTINCLUDEDPRICE) {
        if (!(nitroxNotIncluded || l15NotIncluded || gearNotIncluded || computerNotIncluded || weightsNotIncluded || feesIncluded || feesNotIncluded)) {
          return STEPS.REQUIREMENTS;
        } else {
          return currentStep;
        }
      } else {
        return currentStep;
      }
    });
  }

  const userData = {
    type: currentUser?.userType,
    currency: currentUser?.currency,
  };

  const urlWithParamsProfile = `/editprofile/${currentUser?.id}?userData=${encodeURIComponent(
    JSON.stringify(userData)
  )}`;

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    if (!currentUser?.diveCenter?.city || !currentUser?.diveCenter.country) {
      toast.error(`Please complete your profile before starting to create the listing`);
      setIsLoading(false);
      rentModal.onClose();
      try {
        router.push(urlWithParamsProfile);
      } catch (error) {
        console.error('Error while redirecting to profile:', error);
      }
      return;
    }

    if (step !== STEPS.REQUIREMENTS) {
      return onNext();
    }

    setIsLoading(true);

    if (!price || !category) {
      toast.error(`Please define a ${!price ? 'price' : 'category'} to create the listing`);
      setIsLoading(false);
      return;
    }

    // console.log('data', data);
    axios.post('/api/listings', data)
      .then((response) => {
        const newListingId = response.data.id;
        const editListingUrl = `/editlistingdates/${newListingId}`;

        toast.success('Listing created!');
        router.refresh();

        reset();
        setSelectedIncluded([]);
        setSelectedNotIncluded([]);
        setOtherIncluded('');
        setOtherNotIncluded('');
        setStep(STEPS.CATEGORY)
        rentModal.onClose();
        router.push(editListingUrl);
      })
      .catch(() => {
        toast.error('Something went wrong.');
      })
      .finally(() => {
        setIsLoading(false);
      })
  }

  const actionLabel = useMemo(() => {
    if (step === STEPS.REQUIREMENTS) {
      return 'Create'
    }

    return 'Next'
  }, [step]);

  const secondaryActionLabel = useMemo(() => {
    if (step === STEPS.CATEGORY) {
      return undefined
    }

    return 'Back'
  }, [step]);

  let bodyContent = (
    <div className="flex flex-col gap-8 mb-4">
      <Heading
        title="Which of these best describes your course?"
        subtitle="Pick a category"
      />
      <div
        className="
          grid 
          grid-cols-1 
          md:grid-cols-2 
          gap-3
          max-h-[50vh]
          overflow-y-auto
          scrollbar 
          scrollbar-thumb-gray-400 
          scrollbar-track-gray-300
        "
      >
        {categories.map((item) => (
          <div key={item.label} className="col-span-1">
            <CategoryInput
              onClick={(category) => {
                setCustomValue('category', category);
              }}
              selected={category === item.label}
              label={item.label}
              icon={item.icon}
            />
          </div>
        ))}
      </div>
      {categories.length - 1 && !category && (
        <div className="text-sm text-red-500">This field is required</div>
      )}
    </div>
  )

  if (step === STEPS.DESCRIPTION) {
    bodyContent = (
      <div className="flex flex-col gap-8 mb-4">
        <Heading
          title="How would you describe this course?"
          subtitle="Short and sweet works best!"
        />
        <Input
          id="title"
          label="Title"
          disabled={isLoading}
          register={register}
          errors={errors}
          required
        />
        <hr />
        <Textarea
          id="description"
          label="Description"
          disabled={isLoading}
          register={register}
          errors={errors}
          required
        />
      </div>
    )
  }

  if (step === STEPS.IMAGES) {
    bodyContent = (
      <div className="flex flex-col gap-8 mb-4">
        <Heading
          title="Add a photo of your course"
          subtitle="Show divers what your course looks like!"
        />
        <ListingPhotoUpload
          onChange={(value) => setCustomValue('imageSrc', value)}
          value={imageSrc}
          setIsUploading={setIsUploading}
        />
      </div>
    )
  }

  if (step === STEPS.INFO) {
    bodyContent = (
      <div className="flex flex-col gap-8 mb-4">
        <Heading
          title="Share some basics"
          subtitle="Tell divers what is the activity key info"
        />
        <div>
          <div
            className="relative text-sm"
            id="timepicker-inline-24"
            data-te-input-wrapper-init>
            <label
              htmlFor="form3"
              className="left-3 top-3 text-black font-medium"
            >Start time</label>
            <input
              id="form3"
              type="time"
              onChange={onChangeTime}
              value={startTime}
              className="mt-1 peer block min-h-[auto] w-full rounded border-gray-300 border px-3 py-[0.32rem]"
              disabled={!isTimeDetermined}
            />
          </div>

          <div className="mt-3 text-black font-medium text-sm">
            <label className="mr-3">
              <input
                type="radio"
                name="timeDetermined"
                value="specified"
                checked={isTimeDetermined}
                onChange={onToggleTimeDetermined}
                className="mr-1"
              />
              Specify time
            </label>
            <label>
              <input
                type="radio"
                name="timeDetermined"
                value="determined"
                checked={!isTimeDetermined}
                onChange={onToggleTimeDetermined}
                className="mr-1"
              />
              To be determined
            </label>
          </div>
        </div>
        <div>
          <Counter
            onChange={(value) => {
              setCustomValue('length', value);
            }}
            value={length}
            title="Length"
            subtitle={`How many ${lengthUnit} the activity will last approx?`}
          />
          <div className="my-3 text-black text-sm font-medium">
            <label className="mr-3">
              <input
                type="radio"
                value="hours"
                checked={lengthUnit === 'hours'}
                onChange={() => {
                  // setCustomValue('lengthUnit', 'hours');
                  setUnit('hours');
                }}
                className="mr-1"
              />
              Hours
            </label>
            <label>
              <input
                type="radio"
                value="days"
                checked={lengthUnit === 'days'}
                onChange={() => {
                  // setCustomValue('lengthUnit', 'days');
                  setUnit('days')
                }}
                className="mr-1"
              />
              Days
            </label>
          </div>
          <hr />
          <div className='my-2'>
            <Counter
              onChange={(value) => setCustomValue('students', value)}
              value={students}
              title="Divers"
              subtitle="Maximum number of divers do you allow per group?"
            />
          </div>
          <div className='mt-6'>
            <Counter
              onChange={(value) => setCustomValue('numberOfDives', value)}
              value={numberOfDives}
              title="Number of dives"
              subtitle="How many dives include this experience?"
            />
          </div>
          <div className='flex flex-col mt-6'>

            <label htmlFor="boat" className="text-md">
              Are the dives from the shore, boat or both?
            </label>
            <select
              name="boat"
              value={boat || ""}
              onChange={(event) => setCustomValue('boat', event.target.value)}
              className="border border-gray-300 bg-white px-2 py-1 rounded-lg mt-2 text-sm"
            >
              <option value="" disabled>Select an option...</option>
              <option value="Boat">Boat</option>
              <option value="Shore">Shore</option>
              <option value="Both">Both</option>
            </select>
          </div>
        </div>
      </div>
    )
  }

  if (step === STEPS.PRICE) {
    bodyContent = (
      <div className="flex flex-col gap-3">
        <Heading
          title="Now, set your price"
          subtitle="How much do you charge per course?"
        />
        <div className='flex flex-col'>
          <label htmlFor="currency" className="text-md font-medium">
            Currency
          </label>
          <CurrencySelector
            value={currency}
            onChange={(value) => {
              setCustomValue('currency', value)
            }}
          />
        </div>
        <div className='flex flex-col'>
          <label htmlFor="price" className="text-md font-medium">
            Price
          </label>
          <input
            type="number"
            min="0"
            id="price"
            name="price"
            placeholder="Enter price"
            value={price !== null ? price.toString() : ''}
            onChange={(event) => {
              let newValue = event.target.value;

              if (newValue.startsWith('0') && newValue.length > 1) {
                newValue = newValue.slice(1);
              }
              const newPrice = parseFloat(newValue);
              if (!isNaN(newPrice)) {
                setCustomValue('price', newPrice)
              } else {
                setCustomValue('price', 0)
              }
            }}
            className="border border-gray-300 px-3 py-2 rounded-lg"
          />
        </div>
        {!price && (
          <div className="text-xs text-red-500">This field is required</div>
        )}
        {/* {currency?.value !== 'USD' && (
          <div className='flex flex-col'>
            <label htmlFor="priceUSD" className="text-md font-medium text-gray-600">
              Price in USD
            </label>
            <input
              type="number"
              min="0"
              id="priceUSD"
              name="priceUSD"
              value={priceInUSD}
              className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600"
              disabled
            />
          </div>
        )} */}
        <hr />
        {showExtraFields && (
          <>
            <div className='flex flex-col'>
              <div className="text-md font-bold text-cyan-700 mb-2">
                <div>EXTRA CERTIFIED DIVER?</div>
                <div className='font-light text-sm text-neutral-600 text-justify'>Leave this blank if you don't offer a spot for a certified diver on this experience.</div>
              </div>
              <label htmlFor="price" className="text-md font-medium">
                Price extra diver
              </label>
              <input
                type="number"
                min="0"
                id="price"
                name="price"
                placeholder="Enter price"
                value={priceExtraDiver !== null ? priceExtraDiver.toString() : ''}
                onChange={(event) => {
                  let newValueExtra = event.target.value;

                  if (newValueExtra.startsWith('0') && newValueExtra.length > 1) {
                    newValueExtra = newValueExtra.slice(1);
                  }
                  const newPriceExtra = parseFloat(newValueExtra);
                  if (!isNaN(newPriceExtra)) {
                    setCustomValue('priceExtraDiver', newPriceExtra)
                  } else {
                    setCustomValue('priceExtraDiver', 0)
                  }
                }}
                className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
              />
            </div>
            {/* {currency?.value !== 'USD' && (
              <div className='flex flex-col'>
                <label htmlFor="priceExtraDiverUSD" className="text-md text-gray-600 font-medium">
                  Price extra diver in USD
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceExtraDiverUSD"
                  name="priceExtraDiverUSD"
                  value={priceExtraDiverUSD}
                  className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                  disabled
                />
              </div>
            )} */}

            <div className='flex flex-col'>
              <div className="text-md font-bold text-cyan-700 mb-2">
                <div>EXTRA SNORKELER?</div>
                <div className='font-light text-sm text-neutral-600 text-justify'>Leave this blank if you don't offer a spot for a snorkeler on this experience.</div>
              </div>
              <label htmlFor="priceSnorkeler" className="text-md font-medium">
                Price extra snorkeler
              </label>
              <input
                type="number"
                min="0"
                id="priceSnorkeler"
                name="priceSnorkeler"
                placeholder="Enter price snorkeler"
                value={priceExtraSnorkeler !== null ? priceExtraSnorkeler.toString() : ''}
                onChange={(event) => {
                  let newValueExtra = event.target.value;

                  if (newValueExtra.startsWith('0') && newValueExtra.length > 1) {
                    newValueExtra = newValueExtra.slice(1);
                  }
                  const newPriceExtra = parseFloat(newValueExtra);
                  if (!isNaN(newPriceExtra)) {
                    setCustomValue('priceExtraSnorkeler', newPriceExtra)
                  } else {
                    setCustomValue('priceExtraSnorkeler', 0)
                  }
                }}
                className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
              />
            </div>
            {/* {currency?.value !== 'USD' && (
              <div className='flex flex-col'>
                <label htmlFor="priceExtraSnorkelerUSD" className="text-md text-gray-600 font-medium">
                  Price extra snorkeler in USD
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceExtraSnorkelerUSD"
                  name="priceExtraSnorkelerUSD"
                  value={priceExtraSnorkelerUSD}
                  className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                  disabled
                />
              </div>
            )} */}
          </>
        )}
      </div>
    )
  }

  if (step === STEPS.INCLUDED) {
    bodyContent = (
      <div className="flex flex-col gap-2">
        <Heading
          title="What is included on this course?"
          subtitle="Tell divers what is included and what is not!"
        />
        <label htmlFor="included" className='text-lg font-medium'>Included</label>
        <div>
          <div className="max-h-[20vh] overflow-y-auto scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-300">
            {visibleOptions.map((option) => (
              <div key={option}>
                <input
                  type="checkbox"
                  id={option}
                  name="included"
                  value={option}
                  checked={selectedIncluded.includes(option)}
                  onChange={() => {
                    const updatedSelectedIncluded = selectedIncluded.includes(option)
                      ? selectedIncluded.filter((item) => item !== option)
                      : [...selectedIncluded, option];
                    setSelectedIncluded(updatedSelectedIncluded);

                    const updatedSelectedNotIncluded = selectedNotIncluded.filter((item) => item !== option);
                    setSelectedNotIncluded(updatedSelectedNotIncluded);

                    const updatedIncluded = otherIncluded ? `${updatedSelectedIncluded.join(', ')}, ${otherIncluded}` : updatedSelectedIncluded.join(', ');
                    setCustomValue('included', updatedIncluded);
                    setCustomValue('notIncluded', updatedSelectedNotIncluded.join(', '));
                  }}
                  style={{ accentColor: 'green' }}
                />
                <label htmlFor={option} className='ml-2'>{option}</label>
              </div>
            ))}
          </div>
          {!showAll && options.length > 3 && (
            <button
              className="flex ml-auto bg-gray-300 hover:bg-gray-400 px-4 py-1 rounded-lg"
              onClick={() => setShowAll(true)}>Show More</button>
          )}
          {showAll && (
            <button
              className="flex ml-auto bg-gray-300 hover:bg-gray-400 px-4 py-1 rounded-lg"
              onClick={() => setShowAll(false)}>Show Less</button>
          )}
          <div>
            <input
              type="text"
              id="otherIncluded"
              name="otherIncluded"
              placeholder="Other (please specify)"
              value={otherIncluded}
              onChange={(event) => {
                setOtherIncluded(event.target.value);
                const updatedIncluded = selectedIncluded.length > 0 ? `${selectedIncluded.join(', ')}, ${event.target.value}` : event.target.value;
                setCustomValue('included', updatedIncluded);
              }}
            />
          </div>
        </div>
        <hr />
        <label htmlFor="notIncluded" className='text-lg font-medium'>NOT Included</label>
        <div>
          <div className="max-h-[20vh] overflow-y-auto scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-300">
            {visibleOptions.map((option) => (
              <div key={option}>
                <input
                  type="checkbox"
                  id={`not${option}`}
                  name="notIncluded"
                  value={option}
                  checked={selectedNotIncluded.includes(option)}
                  onChange={() => {
                    const updatedSelectedNotIncluded = selectedNotIncluded.includes(option)
                      ? selectedNotIncluded.filter((item) => item !== option)
                      : [...selectedNotIncluded, option];
                    setSelectedNotIncluded(updatedSelectedNotIncluded);

                    const updatedSelectedIncluded = selectedIncluded.filter((item) => item !== option);
                    setSelectedIncluded(updatedSelectedIncluded);

                    const updatedNotIncluded = otherNotIncluded ? `${updatedSelectedNotIncluded.join(', ')}, ${otherNotIncluded}` : updatedSelectedNotIncluded.join(', ');
                    setCustomValue('notIncluded', updatedNotIncluded);
                    setCustomValue('included', updatedSelectedIncluded.join(', '));
                  }}
                  style={{ accentColor: 'red' }}
                />
                <label htmlFor={`not${option}`} className='ml-2'>{option}</label>
              </div>
            ))}
          </div>
          {!showAll && options.length > 3 && (
            <button
              className="flex ml-auto bg-gray-300 hover:bg-gray-400 px-4 py-1 rounded-lg"
              onClick={() => setShowAll(true)}>Show More</button>
          )}
          {showAll && (
            <button
              className="flex ml-auto bg-gray-300 hover:bg-gray-400 px-4 py-1 rounded-lg"
              onClick={() => setShowAll(false)}>Show Less</button>
          )}
          <div>
            <input
              type="text"
              id="otherNotIncluded"
              name="otherNotIncluded"
              placeholder="Other (please specify)"
              value={otherNotIncluded}
              onChange={(event) => {
                setOtherNotIncluded(event.target.value);
                const updatedNotIncluded = selectedNotIncluded.length > 0 ? `${selectedNotIncluded.join(', ')}, ${event.target.value}` : event.target.value;
                setCustomValue('notIncluded', updatedNotIncluded);
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  if (step === STEPS.NOTINCLUDEDPRICE) {
    bodyContent = (
      <div className="flex flex-col gap-3">
        <Heading
          title="Add-on price"
          subtitle="Set not included items price"
        />
        <div className='flex flex-col'>
          <label htmlFor="currency" className="text-md font-medium">
            Currency
          </label>
          <CurrencySelector
            value={currency}
            onChange={(value) => {
              setCustomValue('currency', value)
            }}
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto scrollbar scrollbar-thumb-gray-400 scrollbar-track-gray-300">
          {nitroxNotIncluded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div className='flex flex-col'>
                <label htmlFor="priceNitrox" className="text-md font-medium">
                  Price Nitrox
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceNitrox"
                  name="priceNitrox"
                  placeholder="Enter nitrox price"
                  value={priceNitrox !== null ? priceNitrox.toString() : ''}
                  onChange={(event) => {
                    let newValueNitrox = event.target.value;

                    if (newValueNitrox.startsWith('0') && newValueNitrox.length > 1) {
                      newValueNitrox = newValueNitrox.slice(1);
                    }
                    const newPriceNitrox = parseFloat(newValueNitrox);
                    if (!isNaN(newPriceNitrox)) {
                      setCustomValue('priceNitrox', newPriceNitrox)
                    } else {
                      setCustomValue('priceNitrox', 0)
                    }
                  }}
                  className="border border-gray-300 px-3 py-2 rounded-lg"
                />
              </div>
              {currency?.value !== 'USD' && (
                <div className='flex flex-col'>
                  <label htmlFor="priceNitroxUSD" className="text-md font-medium text-gray-600">
                    Price nitrox in USD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceNitroxUSD"
                    name="priceNitroxUSD"
                    value={priceNitroxInUSD}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600"
                    disabled
                  />
                </div>
              )}
            </div>
          )}

          {l15NotIncluded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div className='flex flex-col'>
                <label htmlFor="priceFullSet" className="text-md font-medium">
                  Price 15L tank
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceFullSet"
                  name="priceFullSet"
                  placeholder="Enter price 15L"
                  value={price15L !== null ? price15L.toString() : ''}
                  onChange={(event) => {
                    let newValue15L = event.target.value;

                    if (newValue15L.startsWith('0') && newValue15L.length > 1) {
                      newValue15L = newValue15L.slice(1);
                    }
                    const newPrice15L = parseFloat(newValue15L);
                    if (!isNaN(newPrice15L)) {
                      setCustomValue('price15L', newPrice15L)
                    } else {
                      setCustomValue('price15L', 0)
                    }
                  }}
                  className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                />
              </div>
              {currency?.value !== 'USD' && (
                <div className='flex flex-col'>
                  <label htmlFor="price15LInUSD" className="text-md text-gray-600 font-medium">
                    Price 15L in USD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="price15LInUSD"
                    name="price15LInUSD"
                    value={price15LInUSD}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                    disabled
                  />
                </div>
              )}
            </div>
          )}

          {(feesIncluded || feesNotIncluded) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div className='flex flex-col'>
                <label htmlFor="priceFees" className="text-md font-medium">
                  Price Fees
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceFees"
                  name="priceFees"
                  placeholder="Enter nitrox price"
                  value={priceFees !== null ? priceFees.toString() : ''}
                  onChange={(event) => {
                    let newValueFees = event.target.value;

                    if (newValueFees.startsWith('0') && newValueFees.length > 1) {
                      newValueFees = newValueFees.slice(1);
                    }
                    const newPriceFees = parseFloat(newValueFees);
                    if (!isNaN(newPriceFees)) {
                      setCustomValue('priceFees', newPriceFees)
                    } else {
                      setCustomValue('priceFees', 0)
                    }
                  }}
                  className="border border-gray-300 px-3 py-2 rounded-lg"
                />
              </div>
              {currency?.value !== 'USD' && (
                <div className='flex flex-col'>
                  <label htmlFor="priceFeesUSD" className="text-md font-medium text-gray-600">
                    Price fees in USD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceFeesUSD"
                    name="priceFeesUSD"
                    value={priceFeesInUSD}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600"
                    disabled
                  />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
            <div className='flex flex-col'>
              <label htmlFor="pricePrivateGuide" className="text-md font-medium">
                Price Private Guide
              </label>
              <input
                type="number"
                min="0"
                id="pricePrivateGuide"
                name="pricePrivateGuide"
                placeholder="Enter price Private Guide"
                value={pricePrivateGuide !== null ? pricePrivateGuide.toString() : ''}
                onChange={(event) => {
                  let newValuePrivateGuide = event.target.value;

                  if (newValuePrivateGuide.startsWith('0') && newValuePrivateGuide.length > 1) {
                    newValuePrivateGuide = newValuePrivateGuide.slice(1);
                  }
                  const newPricePrivateGuide = parseFloat(newValuePrivateGuide);
                  if (!isNaN(newPricePrivateGuide)) {
                    setCustomValue('pricePrivateGuide', newPricePrivateGuide)
                  } else {
                    setCustomValue('pricePrivateGuide', 0)
                  }
                }}
                className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
              />
            </div>
            {currency?.value !== 'USD' && (
              <div className='flex flex-col'>
                <label htmlFor="pricePrivateGuideInUSD" className="text-md text-gray-600 font-medium">
                  Price Private Guide in USD
                </label>
                <input
                  type="number"
                  min="0"
                  id="pricePrivateGuideInUSD"
                  name="pricePrivateGuideInUSD"
                  value={pricePrivateGuideInUSD}
                  className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                  disabled
                />
              </div>
            )}
          </div>

          {gearNotIncluded && (
            <>
              <div className="text-md font-medium text-cyan-700">Price Scuba Gear</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceFullSet" className="text-md font-medium">
                    Price Full Set
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceFullSet"
                    name="priceFullSet"
                    placeholder="Enter price Full Set"
                    value={priceFullSet !== null ? priceFullSet.toString() : ''}
                    onChange={(event) => {
                      let newValueFullSet = event.target.value;

                      if (newValueFullSet.startsWith('0') && newValueFullSet.length > 1) {
                        newValueFullSet = newValueFullSet.slice(1);
                      }
                      const newPriceFullSet = parseFloat(newValueFullSet);
                      if (!isNaN(newPriceFullSet)) {
                        setCustomValue('priceFullSet', newPriceFullSet)
                      } else {
                        setCustomValue('priceFullSet', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceFullSetInUSD" className="text-md text-gray-600 font-medium">
                      Price Full Set in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceFullSetInUSD"
                      name="priceFullSetInUSD"
                      value={priceFullSetInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceAdapter" className="text-md font-medium">
                    Price Adapter
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceAdapter"
                    name="priceAdapter"
                    placeholder="Enter price Adapter"
                    value={priceAdapter !== null ? priceAdapter.toString() : ''}
                    onChange={(event) => {
                      let newValueAdapter = event.target.value;

                      if (newValueAdapter.startsWith('0') && newValueAdapter.length > 1) {
                        newValueAdapter = newValueAdapter.slice(1);
                      }
                      const newPriceAdapter = parseFloat(newValueAdapter);
                      if (!isNaN(newPriceAdapter)) {
                        setCustomValue('priceAdapter', newPriceAdapter)
                      } else {
                        setCustomValue('priceAdapter', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceAdapterInUSD" className="text-md text-gray-600 font-medium">
                      Price Adapter in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceAdapterInUSD"
                      name="priceAdapterInUSD"
                      value={priceAdapterInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceBCD" className="text-md font-medium">
                    Price BCD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceBCD"
                    name="priceBCD"
                    placeholder="Enter price BCD"
                    value={priceBCD !== null ? priceBCD.toString() : ''}
                    onChange={(event) => {
                      let newValueBCD = event.target.value;

                      if (newValueBCD.startsWith('0') && newValueBCD.length > 1) {
                        newValueBCD = newValueBCD.slice(1);
                      }
                      const newPriceBCD = parseFloat(newValueBCD);
                      if (!isNaN(newPriceBCD)) {
                        setCustomValue('priceBCD', newPriceBCD)
                      } else {
                        setCustomValue('priceBCD', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceBCDInUSD" className="text-md text-gray-600 font-medium">
                      Price BCD in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceBCDInUSD"
                      name="priceBCDInUSD"
                      value={priceBCDInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceReg" className="text-md font-medium">
                    Price Regulator
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceReg"
                    name="priceReg"
                    placeholder="Enter price Regulator"
                    value={priceReg !== null ? priceReg.toString() : ''}
                    onChange={(event) => {
                      let newValueReg = event.target.value;

                      if (newValueReg.startsWith('0') && newValueReg.length > 1) {
                        newValueReg = newValueReg.slice(1);
                      }
                      const newPriceReg = parseFloat(newValueReg);
                      if (!isNaN(newPriceReg)) {
                        setCustomValue('priceReg', newPriceReg)
                      } else {
                        setCustomValue('priceReg', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceRegInUSD" className="text-md text-gray-600 font-medium">
                      Price Regulator in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceRegInUSD"
                      name="priceRegInUSD"
                      value={priceRegInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceWetsuit" className="text-md font-medium">
                    Price Wetsuit
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceWetsuit"
                    name="priceWetsuit"
                    placeholder="Enter price Wetsuit"
                    value={priceWetsuit !== null ? priceWetsuit.toString() : ''}
                    onChange={(event) => {
                      let newValueWetsuit = event.target.value;

                      if (newValueWetsuit.startsWith('0') && newValueWetsuit.length > 1) {
                        newValueWetsuit = newValueWetsuit.slice(1);
                      }
                      const newPriceWetsuit = parseFloat(newValueWetsuit);
                      if (!isNaN(newPriceWetsuit)) {
                        setCustomValue('priceWetsuit', newPriceWetsuit)
                      } else {
                        setCustomValue('priceWetsuit', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceWetsuitInUSD" className="text-md text-gray-600 font-medium">
                      Price Wetsuit in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceWetsuitInUSD"
                      name="priceWetsuitInUSD"
                      value={priceWetsuitInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceMask" className="text-md font-medium">
                    Price Mask
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceMask"
                    name="priceMask"
                    placeholder="Enter price Mask"
                    value={priceMask !== null ? priceMask.toString() : ''}
                    onChange={(event) => {
                      let newValueMask = event.target.value;

                      if (newValueMask.startsWith('0') && newValueMask.length > 1) {
                        newValueMask = newValueMask.slice(1);
                      }
                      const newPriceMask = parseFloat(newValueMask);
                      if (!isNaN(newPriceMask)) {
                        setCustomValue('priceMask', newPriceMask)
                      } else {
                        setCustomValue('priceMask', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceMaskInUSD" className="text-md text-gray-600 font-medium">
                      Price Mask in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceMaskInUSD"
                      name="priceMaskInUSD"
                      value={priceMaskInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                <div className='flex flex-col'>
                  <label htmlFor="priceFins" className="text-md font-medium">
                    Price Fins
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceFins"
                    name="priceFins"
                    placeholder="Enter price Fins"
                    value={priceFins !== null ? priceFins.toString() : ''}
                    onChange={(event) => {
                      let newValueFins = event.target.value;

                      if (newValueFins.startsWith('0') && newValueFins.length > 1) {
                        newValueFins = newValueFins.slice(1);
                      }
                      const newPriceFins = parseFloat(newValueFins);
                      if (!isNaN(newPriceFins)) {
                        setCustomValue('priceFins', newPriceFins)
                      } else {
                        setCustomValue('priceFins', 0)
                      }
                    }}
                    className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                  />
                </div>
                {currency?.value !== 'USD' && (
                  <div className='flex flex-col'>
                    <label htmlFor="priceFinsInUSD" className="text-md text-gray-600 font-medium">
                      Price Fins in USD
                    </label>
                    <input
                      type="number"
                      min="0"
                      id="priceFinsInUSD"
                      name="priceFinsInUSD"
                      value={priceFinsInUSD}
                      className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                      disabled
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {computerNotIncluded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div className='flex flex-col'>
                <label htmlFor="priceComputer" className="text-md font-medium">
                  Price Computer
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceComputer"
                  name="priceComputer"
                  placeholder="Enter price computer"
                  value={priceComputer !== null ? priceComputer.toString() : ''}
                  onChange={(event) => {
                    let newValueComputer = event.target.value;

                    if (newValueComputer.startsWith('0') && newValueComputer.length > 1) {
                      newValueComputer = newValueComputer.slice(1);
                    }
                    const newPriceComputer = parseFloat(newValueComputer);
                    if (!isNaN(newPriceComputer)) {
                      setCustomValue('priceComputer', newPriceComputer)
                    } else {
                      setCustomValue('priceComputer', 0)
                    }
                  }}
                  className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                />
              </div>
              {currency?.value !== 'USD' && (
                <div className='flex flex-col'>
                  <label htmlFor="priceComputerInUSD" className="text-md text-gray-600 font-medium">
                    Price Computer in USD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceComputerInUSD"
                    name="priceComputerInUSD"
                    value={priceComputerInUSD}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                    disabled
                  />
                </div>
              )}
            </div>
          )}

          {weightsNotIncluded && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div className='flex flex-col'>
                <label htmlFor="priceWeights" className="text-md font-medium">
                  Price Weights
                </label>
                <input
                  type="number"
                  min="0"
                  id="priceWeights"
                  name="priceWeights"
                  placeholder="Enter price computer"
                  value={priceWeights !== null ? priceWeights.toString() : ''}
                  onChange={(event) => {
                    let newValueWeights = event.target.value;

                    if (newValueWeights.startsWith('0') && newValueWeights.length > 1) {
                      newValueWeights = newValueWeights.slice(1);
                    }
                    const newPriceWeights = parseFloat(newValueWeights);
                    if (!isNaN(newPriceWeights)) {
                      setCustomValue('priceWeights', newPriceWeights)
                    } else {
                      setCustomValue('priceWeights', 0)
                    }
                  }}
                  className="mt-2 border border-gray-300 px-3 py-2 rounded-lg"
                />
              </div>
              {currency?.value !== 'USD' && (
                <div className='flex flex-col'>
                  <label htmlFor="priceWeightsInUSD" className="text-md text-gray-600 font-medium">
                    Price Weights in USD
                  </label>
                  <input
                    type="number"
                    min="0"
                    id="priceWeightsInUSD"
                    name="priceWeightsInUSD"
                    value={priceWeightsInUSD}
                    className="border border-gray-300 px-3 py-2 rounded-lg text-gray-600 mt-2"
                    disabled
                  />
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    )
  }

  if (step === STEPS.REQUIREMENTS) {
    bodyContent = (
      <div className="flex flex-col gap-6">
        <Heading
          title="Requirements"
          subtitle="Is there any requirement to enroll this course?"
        />
        <div>
          <label className="text-lg font-medium">Requirements</label>
          <div>
            <label htmlFor="hasRequirements">
              <input
                type="radio"
                id="hasRequirements"
                name="requirements"
                value="yes"
                checked={!noRequirements}
                onChange={() => setNoRequirements(false)}
                className="mr-2"
              />
              Yes
            </label>
            <label htmlFor="noRequirements" className="ml-3">
              <input
                type="radio"
                id="noRequirements"
                name="requirements"
                value="no"
                checked={noRequirements}
                onChange={() => setNoRequirements(true)}
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>
        {noRequirements ? (
          <div>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                setCustomValue('minAge', '');
                setCustomValue('minCertification', '');
                setCustomValue('minLoggedDives', '');
              }}
            >
              No Requirements
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className='flex flex-col'>
              <label htmlFor="minAge" className="text-md font-medium">
                Minimum Age
              </label>
              <input
                type="number"
                min="0"
                id="minAge"
                name="minAge"
                placeholder="Enter minimum age"
                value={minAge}
                onChange={(event) => setCustomValue('minAge', event.target.value)}
                className="border border-gray-300 px-3 py-2 rounded-lg mt-2"
              />
            </div>
            <div className='flex flex-col'>
              <label htmlFor="minCertification" className="text-lg font-medium">
                Minimum Certification
              </label>
              <select
                name="minCertification"
                value={minCertification || ""}
                onChange={(event) => setCustomValue('minCertification', event.target.value)}
                className="border border-gray-300 bg-white px-3 py-2 rounded-lg mt-2"
              >
                <option value="" disabled>No certification</option>
                <option value="Open Water">Open Water</option>
                <option value="AOW">AOW</option>
                <option value="Rescue">Rescue</option>
                <option value="Divemaster">Divemaster</option>
                <option value="AI">AI</option>
                <option value="Instructor">Instructor</option>
                <option value="Deep">Deep</option>
                <option value="Enriched Air (Nitrox)">Enriched Air (Nitrox)</option>
                <option value="Self-reliant">Self-reliant</option>
                <option value="Dry suit">Dry suit</option>
                <option value="Cave">Cave</option>
                <option value="Sidemount">Sidemount</option>
                <option value="Altitude">Altitude</option>
                <option value="Ice">Ice</option>
                <option value="Wreck">Wreck</option>
                <option value="EFR">EFR</option>
                <option value="CCR (rebreather)">CCR (rebreather)</option>
                <option value="Trimix">Trimix</option>
                <option value="Tec 40">Tec 40</option>
              </select>
            </div>
            <div className='flex flex-col'>
              <label htmlFor="minLoggedDives" className="text-lg font-medium">
                Minimum Logged Dives
              </label>
              <input
                type="number"
                min="0"
                id="minLoggedDives"
                name="minLoggedDives"
                placeholder="Enter minimum logged dives"
                value={minLoggedDives}
                onChange={(event) => setCustomValue('minLoggedDives', event.target.value)}
                className="border border-gray-300 px-3 py-2 rounded-lg mt-2"
              />
            </div>
          </div>
        )}
      </div>
    )
  }


  return (
    <Modal
      disabled={isLoading || isUploading}
      isOpen={rentModal.isOpen}
      title="List your courses!"
      actionLabel={actionLabel}
      onSubmit={handleSubmit(onSubmit)}
      secondaryActionLabel={secondaryActionLabel}
      secondaryAction={step === STEPS.CATEGORY ? undefined : onBack}
      onClose={rentModal.onClose}
      body={bodyContent}
    />
  );
}

export default RentModal;